<template>
  <div class="controls relative">
    <!-- 左側:主複選框 + 下拉選單 -->
    <BaseElCheckbox
      :value="selection.isAllSelected"
      :indeterminate="selection.isIndeterminate"
      :disabled="disabled || loading"
      @change="handleToggleAll"
    />
    <BaseElSelect
      v-model="selectedScope"
      class="selectOption"
      :disabled="disabled || loading"
      @change="handleSelectScope"
    >
      <BaseElSelectOption value="pending">
        {{ displayText.selectOptions.pending }}
      </BaseElSelectOption>
      <BaseElSelectOption value="expired">
        {{ displayText.selectOptions.expired }}
      </BaseElSelectOption>
    </BaseElSelect>

    <!-- 右側:批次按鈕 -->
    <BaseElButton
      type="text"
      class="toggleBtn"
      :disabled="disabled || loading || selection.selectedCount === 0"
      @click="handleTrigger('cancel')"
    >
      <MaterialIcon :size="16">close</MaterialIcon>
      {{ displayText.btn.cancel }}
    </BaseElButton>
    <BaseElButton
      type="text"
      class="toggleBtn"
      :disabled="disabled || loading || selection.selectedCount === 0"
      @click="handleTrigger('mark-replied')"
    >
      <MaterialIcon :size="16">check</MaterialIcon>
      {{ displayText.btn.markReplied }}
    </BaseElButton>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import MaterialIcon from '@/components/MaterialIcon.vue'

/**
 * ReplyPendingToolbar
 * 預約回覆待確認列表的批次控制列元件
 * 符合 Confluence 4158062596 §5-2 單頁批次規則
 */
export default defineComponent({
  name: 'ReplyPendingToolbar',
  components: {
    MaterialIcon,
  },
  props: {
    /**
     * 選取狀態物件
     * @type {Object}
     * @property {number} selectedCount - 已選取筆數
     * @property {number} totalOnPage - 當前頁面總筆數
     * @property {boolean} isAllSelected - 是否全選當前頁面
     * @property {boolean} isIndeterminate - 是否部分選取
     */
    selection: {
      type: Object,
      required: true,
      validator: (val) => {
        return (
          typeof val.selectedCount === 'number' &&
          typeof val.isAllSelected === 'boolean' &&
          typeof val.isIndeterminate === 'boolean'
        )
      },
    },
    /**
     * 是否禁用所有控制項
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 是否顯示 loading 狀態
     */
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle-all', 'select-scope', 'trigger'],
  setup (props, { emit }) {
    const selectedScope = ref('')

    const displayText = computed(() => ({
      selectOptions: {
        pending: '未回覆',
        expired: '已逾時',
      },
      btn: {
        markReplied: '預約訂單已回覆',
        cancel: '取消預約訂單',
      },
    }))

    /**
     * 處理主複選框點擊事件
     * @param {boolean} checked - 是否勾選
     * @emits toggle-all
     */
    const handleToggleAll = (checked) => {
      emit('toggle-all', checked)
    }

    /**
     * 處理下拉選單選項點擊事件
     * @param {string} scope - 選取範圍 ('pending' | 'expired' | 'all' | 'clear' | '')
     * @emits select-scope
     */
    const handleSelectScope = (scope) => {
      if (scope) {
        emit('select-scope', scope)
        // 執行完動作後重置選單
        if (scope === 'clear') {
          selectedScope.value = ''
        }
      }
    }

    /**
     * 處理批次動作按鈕點擊事件
     * @param {string} action - 動作類型 ('mark-replied' | 'cancel')
     * @emits trigger
     */
    const handleTrigger = (action) => {
      emit('trigger', action)
    }

    return {
      selectedScope,
      displayText,
      handleToggleAll,
      handleSelectScope,
      handleTrigger,
    }
  },
})
</script>

<style scoped lang="postcss">
.controls {
  @apply flex gap-[10px] bg-white px-[24px] items-center rounded-t-[6px];
}

::v-deep .toggleBtn span {
  @apply flex gap-[6px] items-center;
}

::v-deep .selectOption .el-input {
  @apply w-[25px];
}

::v-deep .selectOption .el-input__inner {
  @apply hidden;
}
</style>
